import React, { useEffect, lazy, Suspense } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';

// Lazy-loaded pages for performance
const ArticleReader = lazy(() => import('./pages/ArticleReader'));
const ArticleEditor = lazy(() => import('./pages/ArticleEditor'));
const ArticlesPage = lazy(() => import('./pages/ArticlesPage'));
const AuthorDashboard = lazy(() => import('./pages/AuthorDashboard'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const AuthorProfile = lazy(() => import('./pages/AuthorProfile'));
const AuthorsPage = lazy(() => import('./pages/AuthorsPage'));
const ApplyAuthorPage = lazy(() => import('./pages/ApplyAuthorPage'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ReadingListPage = lazy(() => import('./pages/ReadingListPage'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-3 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
    </div>
  );
}

function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/articles" element={<ArticlesPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/article/:slug" element={<ArticleReader />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/author/:userId" element={<AuthorProfile />} />
        <Route path="/authors" element={<AuthorsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/apply-author" element={
          <ProtectedRoute><ApplyAuthorPage /></ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute><AuthorDashboard /></ProtectedRoute>
        } />
        <Route path="/editor" element={
          <ProtectedRoute><ArticleEditor /></ProtectedRoute>
        } />
        <Route path="/editor/:articleId" element={
          <ProtectedRoute><ArticleEditor /></ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>
        } />
        <Route path="/reading-list" element={
          <ProtectedRoute><ReadingListPage /></ProtectedRoute>
        } />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    const hideBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
    };
    hideBadge();
    const timer = setInterval(hideBadge, 500);
    return () => clearInterval(timer);
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
